import { PlusIcon } from '@heroicons/react/20/solid';
import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreateListing } from '../_context';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import SafeImage from '@ui/SafeImage';
import { DeletePhotoIcon } from '@c/listing/icons/DeletePhotoIcon';
import { ProductDocument } from 'models/product';
import { cx } from 'class-variance-authority';
import Spinner from '@ui/Spinner';

export default function MobilePhotoSelect() {
  const { form, product } = useCreateListing();

  const { uploadingImage, uploadImages, deleteImage } = useMultiImageUpload({
    id: product.id,
    onUploadCallback: ({ paths, errors }) => {
      const newPaths = paths.slice(0, 10 - product.images.length);
      form.setValue('images', [...product.images, ...newPaths]);

      if (errors.length) {
        form.setError('images', {
          type: 'manual',
          message: errors.join(', '),
        });
      }
    },
    onDeleteCallback: (paths) => form.setValue('images', paths),
    type: 'listing',
  });

  const isUploadDisabled = useMemo(
    () => product.images.length >= 10 || uploadingImage,
    [product.images.length, uploadingImage]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const isImageFile = acceptedFiles[0]?.type.includes('image');

      if (!isImageFile) {
        form.setError('images', {
          type: 'manual',
          message: 'Only images are allowed',
        });
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, 10 - product.images.length);

      uploadImages(filesToUpload).then(({ paths, errors }) => {
        if (paths.length) {
          form.setValue('images', [...product.images, ...paths]);
          form.trigger('images'); // Trigger form update
        }

        if (errors.length) {
          form.setError('images', {
            type: 'manual',
            message: errors.join(', '),
          });
        }
      });
    },
    [form, product.images, uploadImages]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: isUploadDisabled,
  });

  return (
    <div className="flex w-full flex-wrap items-center gap-4">
      <AddPhotoBlock
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isUploadDisabled={isUploadDisabled}
        uploadingImage={uploadingImage}
      />

      {product.images.map((image, index) => (
        <PhotoBlock
          key={image?.thumb ?? 'key-' + index}
          image={image}
          index={index}
          deleteImage={() => deleteImage(image, product.images)}
          onMainImageSelect={(paths) => {
            form.setValue('images', paths);
            form.trigger('images'); // Trigger form update
          }}
          images={product.images}
        />
      ))}

      {Array(Math.max(5 - product.images.length, 0))
        .fill(null)
        .map((_, index) => (
          <EmptyPhotoBlock key={index} />
        ))}
    </div>
  );
}

const AddPhotoBlock = ({
  getRootProps,
  getInputProps,
  isUploadDisabled,
  uploadingImage,
}: {
  getRootProps: () => any;
  getInputProps: () => any;
  isUploadDisabled: boolean;
  uploadingImage: boolean;
}) => {
  return (
    <div
      {...getRootProps()}
      className={cx(
        'flex h-[10rem] w-[10rem] items-center justify-center rounded-2xl border-2 border-dashed',
        isUploadDisabled
          ? 'border-zinc-300 opacity-50'
          : 'cursor-pointer border-brand-secondary'
      )}
    >
      <input
        {...getInputProps()}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        disabled={isUploadDisabled}
      />

      {uploadingImage ? (
        <div className="flex h-[10rem] w-[10rem] items-center justify-center">
          <div className="flex h-full w-full items-center justify-center">
            <Spinner className="h-8 w-8" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-y-2">
          <PlusIcon className="h-[4rem] w-[4rem] text-brand-secondary" />
        </div>
      )}
    </div>
  );
};

const EmptyPhotoBlock = () => {
  return (
    <div className="h-[10rem] w-[10rem] rounded-2xl border-2 border-dashed border-zinc-300" />
  );
};

const PhotoBlock = ({
  image,
  index,
  deleteImage,
  onMainImageSelect,
  images,
}: {
  image: ProductDocument['images'][0];
  index: number;
  deleteImage: () => void;
  onMainImageSelect: (paths: ProductDocument['images'][0][]) => void;
  images: ProductDocument['images'][0][];
}) => {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', index.toString());
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={(e) => {
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        const dropIndex = index;
        const newImages = [...images];
        const draggedImage = newImages[dragIndex];
        newImages.splice(dragIndex, 1);
        newImages.splice(dropIndex, 0, draggedImage);
        onMainImageSelect(newImages);
      }}
      onClick={() => {
        const newImages = [...images];
        const draggedImage = newImages[index];
        if (!draggedImage) return;
        newImages.splice(index, 1);
        newImages.splice(0, 0, draggedImage);
        onMainImageSelect(newImages);
      }}
      className={cx([
        'relative aspect-square w-[10rem] cursor-pointer rounded-2xl shadow-md',
        index === 0 ? 'border-4 border-brand-secondary' : '',
      ])}
    >
      <SafeImage
        alt="product image"
        className="rounded-2xl object-cover"
        src={image.download_url}
        fallbackSrc={image.thumb}
        fill
      />
      <button
        type="button"
        className="absolute right-[.6rem] top-[.6rem] z-20 flex flex-col items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          deleteImage();
        }}
      >
        <DeletePhotoIcon height={20} width={20} />
      </button>
    </div>
  );
};
